import { motion } from "framer-motion";
import { Sparkles, Bot } from "lucide-react";

const PROMPTS = [
  "Find me 100 podcasts for DELF B2.",
  "Show my 20 highest-potential YouTube opportunities.",
  "What should I offer this creator?",
  "Write a personalized partnership proposal.",
  "Which partners generated the most revenue last month?",
  "Find more prospects similar to my top-performing partner.",
];

export default function Assistant() {
  return (
    <section id="assistant" data-testid="assistant-section" className="border-t border-white/10">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 py-24 lg:grid-cols-2 lg:items-center lg:px-10 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          data-testid="assistant-widget"
          className="order-2 border border-[#27272A] bg-[#0A0A0A] lg:order-1"
        >
          <div className="flex items-center gap-3 border-b border-white/10 px-8 py-5">
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
              <Bot className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.5} />
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-400">
              Central AI Assistant
            </span>
            <span className="ml-auto flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#10B981]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> Online
            </span>
          </div>
          <div className="space-y-5 px-8 py-8">
            <div className="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-[#D4AF37] px-5 py-3.5 text-sm font-medium text-black">
              Which Facebook groups should I prioritize this week?
            </div>
            <div className="max-w-[92%] rounded-2xl rounded-tl-sm border border-white/10 bg-white/5 px-5 py-4 text-sm leading-relaxed text-zinc-300">
              Based on engagement velocity and audience fit, prioritize{" "}
              <span className="font-semibold text-white">DELF B2 Candidates</span> (92/100) and{" "}
              <span className="font-semibold text-white">Français pour Professionnels</span>{" "}
              (88/100). The B2 group&rsquo;s admin posted about exam anxiety yesterday — a strong
              opening to offer your free masterclass. I&rsquo;ve drafted both approaches for your
              approval.
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              {PROMPTS.slice(0, 4).map((p) => (
                <span
                  key={p}
                  className="cursor-default rounded-full border border-white/15 px-3.5 py-1.5 text-[11px] text-zinc-400"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="order-1 lg:order-2"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D4AF37]">
            Your AI Business Development Department
          </p>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
            Ask in plain language. Act with precision.
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-400">
            Nine specialized agents — Discovery, Research, Scoring, Value, Outreach, Relationship,
            Lead, Analytics and Strategy — operate across your entire pipeline through one
            conversational advisor.
          </p>
          <ul className="mt-8 space-y-3">
            {PROMPTS.slice(2).map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-zinc-300">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />
                &ldquo;{p}&rdquo;
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
